import { VawtPhase } from './vawt-tracker.model';

export const VAWT_PHASES: VawtPhase[] = [
  {
    id: 'phase-1',
    name: 'Phase 1: Design & Specification',
    description: 'Finalize all dimensions, pole count, PCB stator trace geometry, rotor magnet layout, blade profile, and shaft spec before any fabrication begins.',
    tasks: [
      { id: 'p1-blade-research', label: 'Research VAWT blade profiles (Savonius vs Darrieus vs hybrid) and select geometry' },
      { id: 'p1-rpm-calc', label: 'Calculate target RPM range at 5–7 m/s wind for blade design' },
      { id: 'p1-pole-count', label: 'Determine pole count and magnet size for 100W target at operating RPM' },
      { id: 'p1-stator-geometry', label: 'Calculate required stator trace geometry (turns, trace width, copper weight) for target voltage/current' },
      { id: 'p1-shaft-spec', label: 'Finalize shaft diameter, length, and bearing spec' },
      { id: 'p1-rotor-dims', label: 'Finalize rotor disk dimensions (diameter, thickness, magnet pocket layout)' },
      { id: 'p1-stack-height', label: 'Finalize overall stack height for single stage (with room for second stage)' },
      { id: 'p1-master-drawing', label: 'Create master dimension drawing tying all components together' },
      { id: 'p1-magnets-order', label: 'Select magnets (grade, size, quantity) and order' },
    ]
  },
  {
    id: 'phase-2',
    name: 'Phase 2: PCB Stator',
    description: 'Design and order the PCB stator — trace routing, copper weight, thermal limits, connector pads. Validate electrically before mechanical integration.',
    tasks: [
      { id: 'p2-trace-layout', label: 'Design stator coil trace layout in KiCad (or equivalent)' },
      { id: 'p2-trace-validate', label: 'Validate trace width vs current capacity and thermal limits' },
      { id: 'p2-output-design', label: 'Design 3-phase or single-phase output with rectifier pads' },
      { id: 'p2-mount-holes', label: 'Add mounting holes aligned to frame design' },
      { id: 'p2-stackable', label: 'Design second stator as stackable variant (shared hole pattern)' },
      { id: 'p2-order', label: 'Order PCBs (OSHPark, JLCPCB, etc.)' },
      { id: 'p2-test-continuity', label: 'Test PCB resistance and continuity on arrival' },
      { id: 'p2-bench-test', label: 'Bench test PCB stator output (spin with drill, measure voltage)' },
    ]
  },
  {
    id: 'phase-3',
    name: 'Phase 3: Rotor & Shaft',
    description: 'Turn the shaft on the lathe, machine the permanent magnet rotor disk(s), press/bond magnets into place.',
    tasks: [
      { id: 'p3-shaft-turn', label: 'Turn shaft to spec on lathe (diameter, shoulders, keyway if needed)' },
      { id: 'p3-rotor-machine', label: 'Machine rotor disk(s) from stock (steel or aluminum)' },
      { id: 'p3-magnet-pockets', label: 'Machine magnet pockets to spec' },
      { id: 'p3-magnet-bond', label: 'Bond/press magnets into rotor with correct polarity alternation' },
      { id: 'p3-balance', label: 'Balance rotor after magnet installation' },
      { id: 'p3-bearings', label: 'Install bearings on shaft and verify smooth rotation' },
    ]
  },
  {
    id: 'phase-4',
    name: 'Phase 4: Blades & Hub',
    description: 'Design blade profile, machine or 3D print blades, fabricate hub and blade attachment hardware.',
    tasks: [
      { id: 'p4-blade-model', label: 'Model blade profile in CAD' },
      { id: 'p4-blade-print', label: '3D print or machine prototype blade set' },
      { id: 'p4-hub-design', label: 'Design hub with blade attachment points' },
      { id: 'p4-hub-fabricate', label: 'Fabricate hub' },
      { id: 'p4-blade-attach', label: 'Attach blades to hub and check balance' },
      { id: 'p4-spin-test', label: 'Spin test blade/hub assembly for vibration' },
    ]
  },
  {
    id: 'phase-5',
    name: 'Phase 5: Frame & Assembly',
    description: 'Build the main frame that ties shaft, bearing housings, stator mount, and rotor together. Assemble full generator stack.',
    tasks: [
      { id: 'p5-frame-design', label: 'Design main frame (bearing housings, stator mount, rotor clearance)' },
      { id: 'p5-frame-fabricate', label: 'Fabricate frame' },
      { id: 'p5-assemble', label: 'Assemble shaft, bearings, rotor, and stator into frame' },
      { id: 'p5-air-gap', label: 'Verify air gap between stator and rotor (consistent around full rotation)' },
      { id: 'p5-blade-attach', label: 'Attach blade/hub assembly to shaft' },
      { id: 'p5-spin-check', label: 'Full spin check — confirm free rotation, no contact points' },
    ]
  },
  {
    id: 'phase-6',
    name: 'Phase 6: Bench Testing',
    description: 'Spin test with drill/fan, measure open-circuit voltage, short-circuit current, rectified output. Validate against 100W target and iterate.',
    tasks: [
      { id: 'p6-oc-voltage', label: 'Spin with cordless drill, measure open-circuit AC voltage at multiple RPMs' },
      { id: 'p6-sc-current', label: 'Measure short-circuit current' },
      { id: 'p6-dc-output', label: 'Add bridge rectifier, measure DC output' },
      { id: 'p6-power-calc', label: 'Calculate power output, compare to 100W target' },
      { id: 'p6-iterate', label: 'Identify limiting factors (resistance, magnet strength, pole count) and iterate' },
      { id: 'p6-document', label: 'Document final electrical characteristics' },
    ]
  },
  {
    id: 'phase-7',
    name: 'Phase 7: Tower & Mounting',
    description: 'Design and fabricate shed roof mount and tower structure.',
    tasks: [
      { id: 'p7-load-check', label: 'Determine shed roof load capacity and attachment points' },
      { id: 'p7-tower-design', label: 'Design tower structure (height, pipe/tube spec, guy wire plan if needed)' },
      { id: 'p7-tower-fabricate', label: 'Fabricate tower' },
      { id: 'p7-mount-plate', label: 'Design and fabricate roof mount plate/flashing' },
      { id: 'p7-test-fit', label: 'Test fit tower on roof before turbine attachment' },
    ]
  },
  {
    id: 'phase-8',
    name: 'Phase 8: Outdoor Install & Commissioning',
    description: 'Install on shed roof, connect charge controller and battery, monitor real-world output.',
    tasks: [
      { id: 'p8-mount-tower', label: 'Mount tower to shed roof' },
      { id: 'p8-mount-turbine', label: 'Mount turbine to tower' },
      { id: 'p8-wiring', label: 'Run wiring from turbine to charge controller and battery' },
      { id: 'p8-commission', label: 'Commission charge controller settings for 12V system' },
      { id: 'p8-monitor', label: 'Monitor output over first week — log voltage, current, wind conditions' },
      { id: 'p8-document', label: 'Document lessons learned and v2 improvements' },
    ]
  },
];
